<!--
  PendingAccountBanner — shown across the employer shell whenever the current
  session is authenticated but not approved. Reads state from the Pinia
  employerSession store so it appears consistently on every child route.

  Three states, not one. Approval used to be a single yes/no flag, so a declined
  employer and a suspended one were both told their account was "under review"
  and waited for an email that was never coming.
-->
<script setup lang="ts">
import { computed } from 'vue';
import { useEmployerSessionStore } from '@/stores/employerSession';

const session = useEmployerSessionStore();
const show = computed(() => session.needsApproval);
</script>

<template>
    <div v-if="show" class="jb-pending-banner" :class="{ 'jb-pending-banner--stopped': session.isRejected || session.isSuspended }" role="status">
        <div class="jb-pending-banner__icon" aria-hidden="true">
            <Icon :name="session.isPendingReview ? 'clock' : 'circle-alert'" :size="20" />
        </div>

        <div class="jb-pending-banner__body">
            <template v-if="session.isRejected">
                <strong>We could not approve this account.</strong>
                Your company profile is saved, but you cannot post listings. If you think this
                was a mistake, or your details have changed, get in touch and we will take
                another look.
            </template>

            <template v-else-if="session.isSuspended">
                <strong>Your account is paused.</strong>
                Any live listings have been taken down while we review the account. Get in touch
                and we will walk through it with you.
            </template>

            <template v-else>
                <strong>Your account is under review.</strong>
                You can set up your company profile while we take a look. We'll email you the
                moment you're approved to post listings.
            </template>
        </div>
    </div>
</template>

<style scoped>
/* Matches the .emp-attention-banner pattern from employer.css — warning-tinted
   fill using var(--jb-warning-rgb) tokens so the palette stays centralized. */
.jb-pending-banner {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 16px auto;
    padding: 12px 16px;
    max-width: 1200px;
    background: rgba(var(--jb-warning-rgb), 0.08);
    border: 1px solid rgba(var(--jb-warning-rgb), 0.25);
    border-radius: var(--jb-radius);
    color: var(--jb-ink);
    font-size: 14px;
    line-height: 1.45;
}
/* Rejected and suspended are settled outcomes rather than something in progress,
   so they read in the danger tone instead of the waiting one. */
.jb-pending-banner--stopped {
    background: rgba(var(--jb-danger-rgb), 0.08);
    border-color: rgba(var(--jb-danger-rgb), 0.25);
}
.jb-pending-banner__icon {
    display: inline-flex;
    color: var(--jb-warning);
    flex-shrink: 0;
}
.jb-pending-banner--stopped .jb-pending-banner__icon {
    color: var(--jb-danger);
}
.jb-pending-banner__body strong {
    margin-right: 6px;
}
</style>
